import { useEffect, useRef, useState } from 'react';

interface ResizableSplitPaneState {
  editorWidth: number;
  isDragging: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: (e: React.MouseEvent) => void;
}

/** Drag-to-resize a two-pane layout, constrained to 30%-70% of the space left after a fixed-width side panel. */
export const useResizableSplitPane = (
  sidePanelCollapsed: boolean,
): ResizableSplitPaneState => {
  const [editorWidth, setEditorWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      const sidePanelWidth = sidePanelCollapsed
        ? 80
        : containerRect.width * 0.2;
      const gapWidth = 16;
      const availableWidth = containerWidth - sidePanelWidth - gapWidth * 2;

      const relativeX = mouseX - sidePanelWidth - gapWidth;
      let newWidth = (relativeX / availableWidth) * 100;

      newWidth = Math.max(30, Math.min(70, newWidth));

      setEditorWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', () => {});
      document.removeEventListener('mouseup', () => {});
    };
  }, []);

  return { editorWidth, isDragging, containerRef, handleMouseDown };
};
